import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View, type ViewInstance } from 'react-native';

import { GridTypeahead } from '@/components/common/grid-typeahead';
import { LineItemsTable, type LineItemColumn } from '@/components/common/line-items-table';
import { Portal } from '@/components/ui';
import { useAccountTags } from '@/hooks/use-accounting';
import { ACCOUNT_FLAG_CODES, ACCOUNT_FLAG_LABELS, type AccountFlag } from '@/types/models';
import { HAS_EMAIL_TAG, type FilterTag } from '@/utils/mailings-filter';

/** Static fallback when the DB tag registry hasn't loaded: every built-in flag
 *  + the synthetic Has-Email. The component prefers the live registry. */
export const TAG_OPTIONS: FilterTag[] = [...ACCOUNT_FLAG_CODES, HAS_EMAIL_TAG];
export const tagLabel = (t: FilterTag): string => (t === HAS_EMAIL_TAG ? 'Has Email' : (ACCOUNT_FLAG_LABELS[t] ?? t));

export interface AccountTagFilterValue {
	/** Keep ONLY accounts carrying at least one of these tags (OR). Empty = no constraint. */
	includeTags: FilterTag[];
	/** Drop any account carrying at least one of these tags (incl. synthetic Has Email). */
	excludeTags: FilterTag[];
}

export const emptyTagFilter = (): AccountTagFilterValue => ({ includeTags: [], excludeTags: [] });

/** Count of active tag constraints — handy for a "Filters (n)" button badge. */
export const tagFilterCount = (v: AccountTagFilterValue): number => v.includeTags.length + v.excludeTags.length;

type Operator = 'has' | 'exclude';
interface FilterRow {
	key: string;
	op: Operator;
	tag: FilterTag | null;
}

const OPERATOR_LABEL: Record<Operator, string> = { has: 'Has tag', exclude: 'Exclude tag' };

const COLUMNS: LineItemColumn[] = [
	{ key: 'op', label: 'Match', width: '40%' },
	{ key: 'tag', label: 'Tag', width: '60%' },
];

// Module-level counter for row keys — no nondeterministic identity.
let ROW_SEQ = 0;
const nextKey = (): string => `tagrow-${ROW_SEQ++}`;

const isRowEmpty = (r: FilterRow): boolean => !r.tag;

/**
 * Excel-style trailing-blank reconciliation (mirrors invoices/new.tsx): the last
 * row is always blank, so typing into it spawns a fresh row below; clearing a
 * row back to empty collapses redundant trailing blanks.
 */
const reconcileTrailingBlank = (rows: FilterRow[]): FilterRow[] => {
	if (rows.length === 0) return [{ key: nextKey(), op: 'has', tag: null }];
	const last = rows[rows.length - 1];
	if (!isRowEmpty(last)) return [...rows, { key: nextKey(), op: 'has', tag: null }];
	let end = rows.length - 1;
	while (end > 0 && isRowEmpty(rows[end - 1])) end--;
	return end === rows.length - 1 ? rows : rows.slice(0, end + 1);
};

const seedRows = (v: AccountTagFilterValue): FilterRow[] => reconcileTrailingBlank([
	...v.includeTags.map((t): FilterRow => ({ key: nextKey(), op: 'has', tag: t })),
	...v.excludeTags.map((t): FilterRow => ({ key: nextKey(), op: 'exclude', tag: t })),
]);

const dedupe = (tags: FilterTag[]): FilterTag[] => Array.from(new Set(tags));

/** Compact dropdown for the operator cell — bare (no border) so the grid supplies it. */
const OperatorCell: React.FC<{ value: Operator; onChange: (op: Operator) => void }> = ({ value, onChange }) => {
	const wrapRef = useRef<ViewInstance>(null);
	const [open, setOpen] = useState(false);
	const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

	const measure = useCallback(() => {
		// Mirror GridTypeahead: getBoundingClientRect on web, measureInWindow on
		// native (RN Views don't expose getBoundingClientRect). Without the native
		// fallback `rect` stays null and the menu never opens on iOS/Android.
		const node = wrapRef.current as unknown as {
			getBoundingClientRect?: () => DOMRect;
			measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void;
		} | null;
		if (!node) return;
		if (Platform.OS === 'web' && typeof node.getBoundingClientRect === 'function') {
			const r = node.getBoundingClientRect();
			setRect({ top: r.bottom, left: r.left, width: r.width });
		} else if (typeof node.measureInWindow === 'function') {
			node.measureInWindow((x, y, w, h) => setRect({ top: y + h, left: x, width: w }));
		}
	}, []);

	useEffect(() => {
		if (!open) return;
		measure();
		if (Platform.OS !== 'web') return;
		const onMove = () => measure();
		window.addEventListener('scroll', onMove, true);
		window.addEventListener('resize', onMove);
		return () => {
			window.removeEventListener('scroll', onMove, true);
			window.removeEventListener('resize', onMove);
		};
	}, [open, measure]);

	return (
		<View ref={wrapRef} style={styles.opWrap}>
			<Pressable style={styles.opTrigger} onPress={() => { measure(); setOpen((o) => !o); }}>
				<Text style={[styles.opText, value === 'exclude' && styles.opTextExclude]}>{OPERATOR_LABEL[value]}</Text>
				<Text style={styles.opCaret}>▾</Text>
			</Pressable>
			{open && rect && (
				<Portal>
					{/* Full-screen catcher closes the menu on outside press. */}
					<Pressable style={styles.opBackdrop} onPress={() => setOpen(false)} />
					<View style={[styles.opMenu, { top: rect.top + 2, left: rect.left, width: Math.max(rect.width, 140) }]}>
						{(['has', 'exclude'] as Operator[]).map((op) => (
							<Pressable
								key={op}
								style={[styles.opOption, op === value && styles.opOptionActive]}
								onPress={() => { onChange(op); setOpen(false); }}
							>
								<Text style={[styles.opOptionLabel, op === 'exclude' && styles.opTextExclude]}>{OPERATOR_LABEL[op]}</Text>
							</Pressable>
						))}
					</View>
				</Portal>
			)}
		</View>
	);
};

/**
 * HubSpot-style tag filter, rendered as a tight Excel grid (matches the invoice
 * line-items composer): each row is `[Match dropdown] [Tag typeahead]`, and the
 * last row is always blank — picking a tag in it spawns the next row.
 *
 * Owns the editing model (`rows`, which may hold the trailing blank) and
 * projects committed rows out via `onChange` as `{ includeTags, excludeTags }`.
 * Seeds once from `value`; remount with a changed `key` to reset.
 */
export const AccountTagFilter: React.FC<{
	value: AccountTagFilterValue;
	onChange: (next: AccountTagFilterValue) => void;
}> = ({ value, onChange }) => {
	const [rows, setRows] = useState<FilterRow[]>(() => seedRows(value));

	// Prefer the editable registry; fall back to built-in codes until it loads.
	const { data: tags } = useAccountTags();
	const tagItems = useMemo<FilterTag[]>(() => {
		const codes = tags && tags.length > 0 ? tags.map((t) => t.code as AccountFlag) : ACCOUNT_FLAG_CODES;
		return [...codes, HAS_EMAIL_TAG];
	}, [tags]);
	const labelFor = useMemo(() => {
		const byCode = new Map((tags ?? []).map((t) => [t.code, t.label]));
		return (t: FilterTag): string => (t === HAS_EMAIL_TAG ? 'Has Email' : (byCode.get(t) ?? ACCOUNT_FLAG_LABELS[t] ?? t));
	}, [tags]);

	const commit = (updater: (prev: FilterRow[]) => FilterRow[]): void => {
		setRows((prev) => {
			const next = reconcileTrailingBlank(updater(prev));
			onChange({
				includeTags: dedupe(next.filter((r) => r.op === 'has' && r.tag).map((r) => r.tag as FilterTag)),
				excludeTags: dedupe(next.filter((r) => r.op === 'exclude' && r.tag).map((r) => r.tag as FilterTag)),
			});
			return next;
		});
	};

	const setOp = (key: string, op: Operator): void => commit((rs) => rs.map((r) => (r.key === key ? { ...r, op } : r)));
	const setTag = (key: string, tag: FilterTag | null): void => commit((rs) => rs.map((r) => (r.key === key ? { ...r, tag } : r)));

	return (
		<View>
			<Text style={styles.help}>Pick a tag to filter. The last row is always blank — choose a tag and a new row appears below.</Text>
			<LineItemsTable<FilterRow>
				columns={COLUMNS}
				lines={rows}
				renderCell={({ line, column }) => {
					if (column.key === 'op') {
						return <OperatorCell value={line.op} onChange={(op) => setOp(line.key, op)} />;
					}
					return (
						<GridTypeahead<FilterTag>
							items={tagItems}
							toOption={(t) => ({ id: t, display: labelFor(t), secondary: labelFor(t) })}
							value={line.tag}
							onSelect={(t) => setTag(line.key, t ?? null)}
							placeholder="Select a tag…"
							showAllOnEmpty
						/>
					);
				}}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	help: { color: '#78909c', fontSize: 12, marginBottom: 10 },
	opWrap: { flex: 1, alignSelf: 'stretch' },
	opTrigger: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 8, paddingVertical: 8, minHeight: 32 },
	opText: { fontSize: 13, color: '#263238', fontWeight: '600' },
	opTextExclude: { color: '#c62828' },
	opCaret: { fontSize: 11, color: '#90a4ae', marginLeft: 6 },
	opBackdrop: { position: (Platform.OS === 'web' ? 'fixed' : 'absolute') as 'absolute', top: 0, left: 0, right: 0, bottom: 0 },
	opMenu: {
		position: (Platform.OS === 'web' ? 'fixed' : 'absolute') as 'absolute',
		zIndex: 99999,
		backgroundColor: '#fff',
		borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6,
		shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 12, shadowOffset: { width: 0, height: 6 },
		elevation: 24, overflow: 'hidden',
	},
	opOption: { paddingVertical: 9, paddingHorizontal: 12 },
	opOptionActive: { backgroundColor: '#e3f2fd' },
	opOptionLabel: { fontSize: 13, color: '#263238', fontWeight: '600' },
});
